/**
 * Properties API Client
 * Handles all communication with the Property API routes on document-service
 * Routes are project-scoped under /api/projects/:projectId/properties
 */

const PROJECTS_API_BASE = '/api/projects';
const DEFAULT_TIMEOUT = 30000;

async function fetchWithTimeout(url: string, options: RequestInit, timeout = DEFAULT_TIMEOUT): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetchWithTimeout(endpoint, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || response.statusText);
  }

  if (response.status === 204) return null as T;
  return response.json();
}

// ============================================================================
// Types
// ============================================================================

export type PropertyType =
  | 'title' | 'rich_text' | 'number' | 'select' | 'multi_select'
  | 'status' | 'date' | 'people' | 'checkbox' | 'url' | 'email'
  | 'phone' | 'relation' | 'formula' | 'rollup'
  | 'created_time' | 'created_by' | 'last_edited_time' | 'last_edited_by';

export interface PropertyDefinition {
  id: string;
  project_id: string;
  document_type: string | null;
  name: string;
  description: string | null;
  property_type: PropertyType;
  config: Record<string, unknown>;
  order: number;
  is_visible: boolean;
  is_required: boolean;
  default_value: unknown;
  created_at: string;
  updated_at: string;
}

export interface PropertyOption {
  id: string;
  property_def_id: string;
  name: string;
  color: string | null;
  description: string | null;
  display_order: number;
  status_group: string | null;
  is_archived: boolean;
}

export interface DocumentProperty {
  property_id: string;
  property_name: string;
  property_type: PropertyType;
  config: Record<string, unknown>;
  value: unknown;
  options: PropertyOption[] | null;
  is_required: boolean;
  order: number;
}

// ============================================================================
// API
// ============================================================================

export const propertiesApi = {
  // Definitions
  listDefinitions: (projectId: string, documentType?: string): Promise<PropertyDefinition[]> => {
    const params = documentType ? `?document_type=${encodeURIComponent(documentType)}` : '';
    return request(`${PROJECTS_API_BASE}/${projectId}/properties/definitions${params}`);
  },

  createDefinition: (projectId: string, data: {
    name: string;
    property_type: PropertyType;
    document_type?: string;
    description?: string;
    config?: Record<string, unknown>;
    is_required?: boolean;
    default_value?: unknown;
  }): Promise<PropertyDefinition> => {
    return request(`${PROJECTS_API_BASE}/${projectId}/properties/definitions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateDefinition: (projectId: string, propertyId: string, data: {
    name?: string;
    description?: string;
    config?: Record<string, unknown>;
    is_required?: boolean;
    is_visible?: boolean;
    default_value?: unknown;
  }): Promise<PropertyDefinition> => {
    return request(`${PROJECTS_API_BASE}/${projectId}/properties/definitions/${propertyId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteDefinition: (projectId: string, propertyId: string): Promise<void> => {
    return request(`${PROJECTS_API_BASE}/${projectId}/properties/definitions/${propertyId}`, {
      method: 'DELETE',
    });
  },

  reorderDefinitions: (projectId: string, orderedIds: string[]): Promise<void> => {
    return request(`${PROJECTS_API_BASE}/${projectId}/properties/definitions/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ ordered_ids: orderedIds }),
    });
  },

  // Options
  addOption: (projectId: string, propertyId: string, data: {
    name: string;
    color?: string;
    status_group?: string;
  }): Promise<PropertyOption> => {
    return request(`${PROJECTS_API_BASE}/${projectId}/properties/definitions/${propertyId}/options`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateOption: (projectId: string, propertyId: string, optionId: string, data: {
    name?: string;
    color?: string;
    status_group?: string;
  }): Promise<PropertyOption> => {
    return request(`${PROJECTS_API_BASE}/${projectId}/properties/definitions/${propertyId}/options/${optionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  archiveOption: (projectId: string, propertyId: string, optionId: string): Promise<void> => {
    return request(`${PROJECTS_API_BASE}/${projectId}/properties/definitions/${propertyId}/options/${optionId}`, {
      method: 'DELETE',
    });
  },

  // Values
  getDocumentProperties: async (projectId: string, documentId: string): Promise<DocumentProperty[]> => {
    try {
      return await request(`${PROJECTS_API_BASE}/${projectId}/properties/values/${documentId}`);
    } catch (err) {
      // 404 is expected when no properties are defined for a project — suppress silently
      if (err instanceof Error && (err.message.includes('404') || err.message.includes('Not Found'))) {
        return [];
      }
      throw err;
    }
  },

  setPropertyValue: (projectId: string, documentId: string, propertyId: string, value: unknown): Promise<unknown> => {
    return request(`${PROJECTS_API_BASE}/${projectId}/properties/values/${documentId}/${propertyId}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  },

  deletePropertyValue: (projectId: string, documentId: string, propertyId: string): Promise<void> => {
    return request(`${PROJECTS_API_BASE}/${projectId}/properties/values/${documentId}/${propertyId}`, {
      method: 'DELETE',
    });
  },
};
